import { Button, Grid, Menu, MenuItem, Stack, Typography } from '@mui/material';
import React, { useEffect, useState, useRef } from 'react';

import FormattedMessages from '../../../../messages/admin/CustomerProfileMessages';
import { TextInput } from './Inputs';
import { capitalize } from '../../../../utils/string';

const ContactInfo = ({ profile, highlighted, onChange }) => {
  const [contacts, setContacts] = useState({});
  const [openedBy, setOpenedBy] = useState(null);
  const anchorRef = useRef(null);

  const categories = ['authorized', 'owner', 'billing', 'technical'];

  useEffect(() => {
    setContacts(profile?.contact || {});
  }, [profile]);

  const handleInputChange = (name, value) => {
    const newValue = { ...contacts };
    newValue[name].value = value;

    setContacts(newValue);
    onChange('contact', newValue);
  };

  const openMenu = (event, category) => {
    anchorRef.current = event.currentTarget;
    setOpenedBy(category);
  };

  const closeMenu = () => {
    setOpenedBy(null);
  };

  const handleCopy = (source) => {
    const newValue = { ...contacts };

    newValue[`${openedBy}_first_name`].value =
      newValue[`${source}_first_name`].value;

    newValue[`${openedBy}_last_name`].value =
      newValue[`${source}_last_name`].value;

    newValue[`${openedBy}_position`].value =
      newValue[`${source}_position`].value;

    newValue[`${openedBy}_contact_email`].value =
      newValue[`${source}_contact_email`].value;

    newValue[`${openedBy}_work_phone`].value =
      newValue[`${source}_work_phone`].value;

    if (newValue[`${openedBy}_mobile`] && newValue[`${source}_mobile`]) {
      newValue[`${openedBy}_mobile`].value = newValue[`${source}_mobile`].value;
    }

    setContacts(newValue);
    onChange('contact', newValue);
    closeMenu();
  };

  return (
    <>
      <Grid container spacing={10} sx={{ width: '100%' }}>
        {/* Left Column */}
        <Grid sx={{ maxWidth: { md: 460 } }} size={{ xs: 12, lg: 6 }}>
          <Stack spacing={3}>
            <Stack direction="row" justifyContent="space-between">
              <Typography variant="h6">
                {FormattedMessages.contactAuthorized}
              </Typography>
              <div>
                <Button
                  size="small"
                  variant="text"
                  aria-controls="copy-menu"
                  aria-haspopup="true"
                  aria-expanded={openedBy === 'authorized'}
                  onClick={(e) => openMenu(e, 'authorized')}
                >
                  {FormattedMessages.copyFrom}
                </Button>
              </div>
            </Stack>

            <TextInput
              id="authorized_first_name"
              name="authorized_first_name"
              label={FormattedMessages.firstName}
              value={contacts.authorized_first_name?.value}
              required={contacts.authorized_first_name?.required}
              onChange={handleInputChange}
              highlighted={highlighted}
            />

            <TextInput
              id="authorized_last_name"
              name="authorized_last_name"
              label={FormattedMessages.lastName}
              value={contacts.authorized_last_name?.value}
              required={contacts.authorized_last_name?.required}
              onChange={handleInputChange}
              highlighted={highlighted}
            />

            <TextInput
              id="authorized_position"
              name="authorized_position"
              label={FormattedMessages.position}
              value={contacts.authorized_position?.value}
              required={contacts.authorized_position?.required}
              onChange={handleInputChange}
              highlighted={highlighted}
            />

            <TextInput
              id="authorized_contact_email"
              name="authorized_contact_email"
              type="email"
              label={FormattedMessages.email}
              value={contacts.authorized_contact_email?.value}
              required={contacts.authorized_contact_email?.required}
              onChange={handleInputChange}
              highlighted={highlighted}
            />

            <TextInput
              id="authorized_work_phone"
              name="authorized_work_phone"
              label={FormattedMessages.phone}
              value={contacts.authorized_work_phone?.value}
              required={contacts.authorized_work_phone?.required}
              onChange={handleInputChange}
              highlighted={highlighted}
            />

            <TextInput
              id="authorized_mobile"
              name="authorized_mobile"
              label={FormattedMessages.mobile}
              value={contacts.authorized_mobile?.value}
              required={contacts.authorized_mobile?.required}
              onChange={handleInputChange}
              highlighted={highlighted}
            />
          </Stack>
        </Grid>
        {/* Right Column */}
        <Grid sx={{ maxWidth: { md: 460 } }} size={{ xs: 12, lg: 6 }}>
          <Stack spacing={3}>
            <Stack direction="row" justifyContent="space-between">
              <Typography variant="h6">
                {FormattedMessages.contactOwner}
              </Typography>
              <div>
                <Button
                  size="small"
                  variant="text"
                  aria-controls="copy-menu"
                  aria-haspopup="true"
                  aria-expanded={openedBy === 'owner'}
                  onClick={(e) => openMenu(e, 'owner')}
                >
                  {FormattedMessages.copyFrom}
                </Button>
              </div>
            </Stack>

            <TextInput
              id="owner_first_name"
              name="owner_first_name"
              label={FormattedMessages.firstName}
              value={contacts.owner_first_name?.value}
              required={contacts.owner_first_name?.required}
              onChange={handleInputChange}
              highlighted={highlighted}
            />

            <TextInput
              id="owner_last_name"
              name="owner_last_name"
              label={FormattedMessages.lastName}
              value={contacts.owner_last_name?.value}
              required={contacts.owner_last_name?.required}
              onChange={handleInputChange}
              highlighted={highlighted}
            />

            <TextInput
              id="owner_position"
              name="owner_position"
              label={FormattedMessages.position}
              value={contacts.owner_position?.value}
              required={contacts.owner_position?.required}
              onChange={handleInputChange}
              highlighted={highlighted}
            />

            <TextInput
              id="owner_contact_email"
              name="owner_contact_email"
              type="email"
              label={FormattedMessages.email}
              value={contacts.owner_contact_email?.value}
              required={contacts.owner_contact_email?.required}
              onChange={handleInputChange}
              highlighted={highlighted}
            />

            <TextInput
              id="owner_work_phone"
              name="owner_work_phone"
              label={FormattedMessages.phone}
              value={contacts.owner_work_phone?.value}
              required={contacts.owner_work_phone?.required}
              onChange={handleInputChange}
              highlighted={highlighted}
            />
          </Stack>
        </Grid>

        {/* Left Column */}
        <Grid sx={{ maxWidth: { md: 460 } }} size={{ xs: 12, lg: 6 }}>
          <Stack spacing={3}>
            <Stack direction="row" justifyContent="space-between">
              <Typography variant="h6">
                {FormattedMessages.contactBilling}
              </Typography>
              <div>
                <Button
                  size="small"
                  variant="text"
                  aria-controls="copy-menu"
                  aria-haspopup="true"
                  aria-expanded={openedBy === 'billing'}
                  onClick={(e) => openMenu(e, 'billing')}
                >
                  {FormattedMessages.copyFrom}
                </Button>
              </div>
            </Stack>

            <TextInput
              id="billing_first_name"
              name="billing_first_name"
              label={FormattedMessages.firstName}
              value={contacts.billing_first_name?.value}
              required={contacts.billing_first_name?.required}
              onChange={handleInputChange}
              highlighted={highlighted}
            />

            <TextInput
              id="billing_last_name"
              name="billing_last_name"
              label={FormattedMessages.lastName}
              value={contacts.billing_last_name?.value}
              required={contacts.billing_last_name?.required}
              onChange={handleInputChange}
              highlighted={highlighted}
            />

            <TextInput
              id="billing_position"
              name="billing_position"
              label={FormattedMessages.position}
              value={contacts.billing_position?.value}
              required={contacts.billing_position?.required}
              onChange={handleInputChange}
              highlighted={highlighted}
            />

            <TextInput
              id="billing_contact_email"
              name="billing_contact_email"
              type="email"
              label={FormattedMessages.email}
              value={contacts.billing_contact_email?.value}
              required={contacts.billing_contact_email?.required}
              onChange={handleInputChange}
              highlighted={highlighted}
            />

            <TextInput
              id="billing_work_phone"
              name="billing_work_phone"
              label={FormattedMessages.phone}
              value={contacts.billing_work_phone?.value}
              required={contacts.billing_work_phone?.required}
              onChange={handleInputChange}
              highlighted={highlighted}
            />
          </Stack>
        </Grid>

        {/* Right Column */}
        <Grid sx={{ maxWidth: { md: 460 } }} size={{ xs: 12, lg: 6 }}>
          <Stack spacing={3}>
            <Stack direction="row" justifyContent="space-between">
              <Typography variant="h6">
                {FormattedMessages.contactTechnical}
              </Typography>
              <div>
                <Button
                  size="small"
                  variant="text"
                  aria-controls="copy-menu"
                  aria-haspopup="true"
                  aria-expanded={openedBy === 'technical'}
                  onClick={(e) => openMenu(e, 'technical')}
                >
                  {FormattedMessages.copyFrom}
                </Button>
              </div>
            </Stack>

            <TextInput
              id="technical_first_name"
              name="technical_first_name"
              label={FormattedMessages.firstName}
              value={contacts.technical_first_name?.value}
              required={contacts.technical_first_name?.required}
              onChange={handleInputChange}
              highlighted={highlighted}
            />

            <TextInput
              id="technical_last_name"
              name="technical_last_name"
              label={FormattedMessages.lastName}
              value={contacts.technical_last_name?.value}
              required={contacts.technical_last_name?.required}
              onChange={handleInputChange}
              highlighted={highlighted}
            />

            <TextInput
              id="technical_position"
              name="technical_position"
              label={FormattedMessages.position}
              value={contacts.technical_position?.value}
              required={contacts.technical_position?.required}
              onChange={handleInputChange}
              highlighted={highlighted}
            />

            <TextInput
              id="technical_contact_email"
              name="technical_contact_email"
              type="email"
              label={FormattedMessages.email}
              value={contacts.technical_contact_email?.value}
              required={contacts.technical_contact_email?.required}
              onChange={handleInputChange}
              highlighted={highlighted}
            />

            <TextInput
              id="technical_work_phone"
              name="technical_work_phone"
              label={FormattedMessages.phone}
              value={contacts.technical_work_phone?.value}
              required={contacts.technical_work_phone?.required}
              onChange={handleInputChange}
              highlighted={highlighted}
            />

            <TextInput
              id="technical_mobile"
              name="technical_mobile"
              label={FormattedMessages.mobile}
              value={contacts.technical_mobile?.value}
              required={contacts.technical_mobile?.required}
              onChange={handleInputChange}
              highlighted={highlighted}
            />
          </Stack>
        </Grid>
      </Grid>

      <Menu
        id="copy-menu"
        anchorEl={anchorRef.current}
        open={Boolean(openedBy)}
        onClose={closeMenu}
        anchorOrigin={{
          vertical: 'bottom',
          horizontal: 'left',
        }}
        transformOrigin={{
          vertical: 'top',
          horizontal: 'left',
        }}
      >
        {categories.map((c) => (
          <MenuItem
            key={c}
            disabled={c === openedBy}
            onClick={() => handleCopy(c)}
          >
            {FormattedMessages[`contact${capitalize(c)}`]}
          </MenuItem>
        ))}
      </Menu>
    </>
  );
};

export default ContactInfo;
